/**
 * Formatters
 *
 * Utility functions for formatting data for display.
 */

// =============================================================================
// NUMBER FORMATTERS
// =============================================================================

/**
 * Format a number as currency
 *
 * @example
 * formatCurrency(1234.56) // '$1,234.56'
 * formatCurrency(1234.56, 'EUR', 'de-DE') // '1.234,56 €'
 */
export function formatCurrency(
  value: number,
  currency: string = 'USD',
  locale: string = 'en-US'
): string {
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
  }).format(value);
}

/**
 * Format a number with thousand separators
 *
 * @example
 * formatNumber(1234567.89) // '1,234,567.89'
 */
export function formatNumber(
  value: number,
  locale: string = 'en-US',
  options?: Intl.NumberFormatOptions
): string {
  return new Intl.NumberFormat(locale, options).format(value);
}

/**
 * Format a number as a percentage
 *
 * @example
 * formatPercent(0.1234) // '12.34%'
 * formatPercent(0.1234, 0) // '12%'
 */
export function formatPercent(
  value: number,
  decimals: number = 2,
  locale: string = 'en-US'
): string {
  return new Intl.NumberFormat(locale, {
    style: 'percent',
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(value);
}

/**
 * Format a number in compact notation
 *
 * @example
 * formatCompact(1234567) // '1.2M'
 */
export function formatCompact(
  value: number,
  locale: string = 'en-US'
): string {
  return new Intl.NumberFormat(locale, {
    notation: 'compact',
    compactDisplay: 'short',
  }).format(value);
}

// =============================================================================
// DATE FORMATTERS
// =============================================================================

/**
 * Format a date for display
 *
 * @example
 * formatDate(new Date()) // 'Jan 1, 2024'
 */
export function formatDate(
  date: Date | string | number,
  options?: Intl.DateTimeFormatOptions,
  locale: string = 'en-US'
): string {
  const dateObj = date instanceof Date ? date : new Date(date);

  const defaultOptions: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    ...options,
  };

  return new Intl.DateTimeFormat(locale, defaultOptions).format(dateObj);
}

/**
 * Format a date with time
 *
 * @example
 * formatDateTime(new Date()) // 'Jan 1, 2024, 12:00 PM'
 */
export function formatDateTime(
  date: Date | string | number,
  locale: string = 'en-US'
): string {
  return formatDate(date, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }, locale);
}

/**
 * Format a date as relative time
 *
 * @example
 * formatRelativeTime(Date.now() - 60000) // '1 minute ago'
 */
export function formatRelativeTime(
  date: Date | string | number,
  locale: string = 'en-US'
): string {
  const dateObj = date instanceof Date ? date : new Date(date);
  const now = new Date();
  const diffInSeconds = Math.floor((now.getTime() - dateObj.getTime()) / 1000);

  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });

  if (Math.abs(diffInSeconds) < 60) {
    return rtf.format(-diffInSeconds, 'second');
  }

  const diffInMinutes = Math.floor(diffInSeconds / 60);
  if (Math.abs(diffInMinutes) < 60) {
    return rtf.format(-diffInMinutes, 'minute');
  }

  const diffInHours = Math.floor(diffInMinutes / 60);
  if (Math.abs(diffInHours) < 24) {
    return rtf.format(-diffInHours, 'hour');
  }

  const diffInDays = Math.floor(diffInHours / 24);
  if (Math.abs(diffInDays) < 30) {
    return rtf.format(-diffInDays, 'day');
  }

  const diffInMonths = Math.floor(diffInDays / 30);
  if (Math.abs(diffInMonths) < 12) {
    return rtf.format(-diffInMonths, 'month');
  }

  const diffInYears = Math.floor(diffInMonths / 12);
  return rtf.format(-diffInYears, 'year');
}

/**
 * Format a date as ISO string (YYYY-MM-DD)
 */
export function formatISODate(date: Date | string | number): string {
  const dateObj = date instanceof Date ? date : new Date(date);
  return dateObj.toISOString().split('T')[0];
}

// =============================================================================
// STRING FORMATTERS
// =============================================================================

/**
 * Truncate a string with ellipsis
 *
 * @example
 * truncate('Hello World', 5) // 'Hello...'
 */
export function truncate(
  str: string,
  maxLength: number,
  suffix: string = '...'
): string {
  if (str.length <= maxLength) {
    return str;
  }
  return str.slice(0, maxLength).trimEnd() + suffix;
}

/**
 * Capitalize the first letter of a string
 *
 * @example
 * capitalize('hello world') // 'Hello world'
 */
export function capitalize(str: string): string {
  if (!str) return str;
  return str.charAt(0).toUpperCase() + str.slice(1);
}

/**
 * Convert string to title case
 *
 * @example
 * titleCase('hello world') // 'Hello World'
 */
export function titleCase(str: string): string {
  return str
    .toLowerCase()
    .split(' ')
    .map((word) => capitalize(word))
    .join(' ');
}

/**
 * Convert camelCase to readable text
 *
 * @example
 * camelToReadable('helloWorld') // 'Hello World'
 */
export function camelToReadable(str: string): string {
  return str
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, (s) => s.toUpperCase())
    .trim();
}

/**
 * Pluralize a word based on count
 *
 * @example
 * pluralize(1, 'item', 'items') // 'item'
 * pluralize(5, 'item', 'items') // 'items'
 */
export function pluralize(
  count: number,
  singular: string,
  plural: string
): string {
  return count === 1 ? singular : plural;
}

// =============================================================================
// FILE SIZE FORMATTERS
// =============================================================================

/**
 * Format bytes to human-readable size
 *
 * @example
 * formatFileSize(1024) // '1 KB'
 * formatFileSize(1048576) // '1 MB'
 */
export function formatFileSize(bytes: number, decimals: number = 2): string {
  if (bytes === 0) return '0 Bytes';

  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(decimals))} ${sizes[i]}`;
}
export const formatUserName = (firstName: string | null, lastName: string | null): string =>
  [firstName, lastName].filter(Boolean).join(' ');

/** A guest's name/email are synthetic, so label them by their access code's tags. */
export const formatGuestLabel = (accessCodeTags: string[]): string =>
  ['Guest candidate', accessCodeTags.join(', ')].filter(Boolean).join(' · ');

/**
 * Format the elapsed time between two timestamps.
 *
 * Example:
 * - formatDuration('2024-01-01T00:00:00Z', '2024-01-01T00:00:01.200Z') // '1.2s'
 * - formatDuration('2024-01-01T00:00:00Z', null) // 'Running'
 */
export function formatDuration(startedAt: string | Date, endedAt: string | Date | null): string {
  if (!endedAt) return 'Running';

  const ms = new Date(endedAt).getTime() - new Date(startedAt).getTime();
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
}

/**
 * Format a score movement against the candidate's previous attempt. Shared by
 * the interview results chart and the HTS Readiness summary so the two can't
 * word the same comparison differently. Null when there's no delta to show.
 *
 * Example:
 * - formatScoreDelta(14)  // '+14 vs last attempt'
 * - formatScoreDelta(0)   // '±0 vs last attempt'
 * - formatScoreDelta(null) // null
 */
export function formatScoreDelta(delta: number | null | undefined): string | null {
  if (delta == null) return null;
  const rounded = Math.round(delta);
  return rounded === 0 ? '±0 vs last attempt' : `${rounded > 0 ? '+' : ''}${rounded} vs last attempt`;
}

/**
 * Format a millisecond duration as a mm:ss clock.
 *
 * Example: formatDurationClock(75000) // '01:15'
 */
export function formatDurationClock(ms: number): string {
  const totalSeconds = Math.round(ms / 1000);
  const m = Math.floor(totalSeconds / 60).toString().padStart(2, '0');
  const s = (totalSeconds % 60).toString().padStart(2, '0');
  return `${m}:${s}`;
}
